import { createContext, useContext, useEffect, useRef, useState } from "react";
import * as React from "react";
import styled, { css, keyframes } from "styled-components";

const animation = keyframes`
  from {
    transform: scale(2.15);
    box-shadow: 0 0 0 3px rgba(82, 102, 255, 0.1);
  }
  to {
    transform: scale(1);
    box-shadow: 0 0 0 3px rgba(82, 102, 255, 1);
  }
`;

interface AnimatedProps {
  animate: boolean;
  shape?: "circle" | "roundedSquare" | string;
}

const AnimatedComponentWrapper = styled.div<AnimatedProps>`
  position: relative;
  border: 2px solid transparent; // Maintain the component layout
  display: inline-block; // Maintain the component layout
  ${({ animate, shape }): any => {
    let borderRadius = "0px"; // default to square

    // predefined shapes
    if (shape === "circle") {
      borderRadius = "50%";
    } else if (shape === "roundedSquare") {
      borderRadius = "10px";
    } else if (shape) {
      // custom shape
      borderRadius = shape;
    }

    return css`
      &::before {
        content: "";
        position: absolute;
        top: -16px; // Adjust based on the desired initial distance of the border
        left: -16px;
        right: -16px;
        bottom: -16px;
        border-radius: ${borderRadius};
        box-shadow: 0 0 0 0 rgba(82, 102, 255, 0); // Initial state with no visible border
        transition: all 1.1s forwards; // Smooth transition for all properties
        display: none; // Start hidden to ensure it's invisible when not animated
        opacity: 0; // Start fully transparent to ensure it's invisible when not animated
        ${animate &&
        css`
          box-shadow: 0 0 0 3px rgba(82, 102, 255, 1); // Final state with visible border
          top: -3px; // Adjust back to match the element's border
          left: -3px;
          right: -3px;
          bottom: -3px;
          opacity: 1; // Fully visible at the end of the animation
          display: block; // Show the border when animated
          animation: ${animation} 0.5s forwards;
        `}
      }
    `;
  }}
`;

interface HoverContextType {
  hoveredComponent: string | null;
  setHoveredComponent: React.Dispatch<React.SetStateAction<string | null>>;
}

const HoverContext = createContext<HoverContextType | undefined>(undefined);

// Hover provider needs to wrap everthing so that the animation can be applied to any component it is used on
export const HoverAnimationProvider: React.FC = ({ children }) => {
  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null);

  return (
    <HoverContext.Provider value={{ hoveredComponent, setHoveredComponent }}>
      {children}
    </HoverContext.Provider>
  );
};

// Custom hook to use the hover context
export const useHoverContext = (): HoverContextType => {
  const context = useContext(HoverContext);
  if (context === undefined) {
    throw new Error(
      "useHoverContext must be used within a HoverAnimationProvider. No sloppy steaks for you!"
    );
  }
  return context;
};

interface AnimatedComponentProps {
  hoverId: string;
  shape?: "circle" | "roundedSquare" | string;
}

export const AnimatedComponent: React.FC<AnimatedComponentProps> = ({
  shape,
  hoverId,
  children,
}) => {
  const { hoveredComponent } = useHoverContext();
  return (
    <AnimatedComponentWrapper
      animate={hoveredComponent === hoverId}
      shape={shape}
    >
      {children}
    </AnimatedComponentWrapper>
  );
};

export const useHoverTrigger = (
  componentId: string
  // may have to update this return type
): React.RefObject<HTMLDivElement> => {
  const { setHoveredComponent } = useHoverContext();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      const handleMouseEnter = (): void => setHoveredComponent(componentId);
      const handleMouseLeave = (): void => setHoveredComponent(null);

      node.addEventListener("mouseenter", handleMouseEnter);
      node.addEventListener("mouseleave", handleMouseLeave);

      return (): void => {
        node.removeEventListener("mouseenter", handleMouseEnter);
        node.removeEventListener("mouseleave", handleMouseLeave);
        handleMouseLeave();
      };
    }
  }, [componentId, setHoveredComponent]);

  return ref;
};
